import React, { useState, useEffect, useRef } from 'react';
import {
  Building, Scaling, Construction, Layout, LandPlot,
  ChevronRight, Camera, CheckCircle2, Fingerprint, Database,
  Zap, AlertTriangle, Image as ImageIcon, History,
  Scale, BrainCircuit, ShieldAlert, UploadCloud, Microscope,
} from 'lucide-react';

import AssetPericialService from '../../services/AssetPericialService';
import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, FATORES_CONFIG, proximaEtapa } from '../../constants/vistoria';

const FATOR_ID = ETAPA.F06_OCUPACAO;
const FATOR_CONFIG = FATORES_CONFIG[FATOR_ID];

const DICIONARIO_ABNT = {
  padraoOcupacao: [
    'ALTO PADRÃO (CONDOMÍNIOS DE LUXO/EDIFÍCIOS GOURMET)',
    'PADRÃO MÉDIO-ALTO (CONDOMÍNIOS CLUB/CASAS AMPLAS)',
    'PADRÃO MÉDIO (CONSTRUÇÕES INTEGRADAS E REGULARES)',
    'PADRÃO ECONÔMICO (HABITAÇÕES COLETIVAS/POPULARES)',
    'PADRÃO POPULAR (CONSTRUÇÕES SIMPLES/ANTIGAS)',
    'ZONA DE OCUPAÇÃO IRREGULAR / PRECÁRIA',
  ],
  verticalizacao: [
    'VERTICALIZAÇÃO INTENSA (TORRES ACIMA DE 15 ANDARES)',
    'VERTICALIZAÇÃO MÉDIA (PRÉDIOS DE ATÉ 10 ANDARES)',
    'PREDOMINÂNCIA DE CASAS (BAIXA DENSIDADE)',
    'VERTICALIZAÇÃO EM CRESCIMENTO (CANTEIROS ATIVOS)',
    'ZONA DE RESTRIÇÃO DE ALTURA (AEROPORTOS/HISTÓRICO)',
    'OCUPAÇÃO HORIZONTAL DISPERSA',
  ],
  conservacaoRegiao: [
    'CONSERVAÇÃO EXCELENTE (ZELADORIA ATIVA)',
    'CONSERVAÇÃO BOA (MANUTENÇÃO PERIÓDICA)',
    'CONSERVAÇÃO REGULAR (DESGASTES NATURAIS)',
    'CONSERVAÇÃO DEFICIENTE (PICHIAÇÃO/DEGRADAÇÃO)',
    'EM PROCESSO DE REVITALIZAÇÃO URBANA',
    'ESTADO DE ABANDONO / CRÍTICO',
  ],
  usoPreponderante: [
    'ESTRITAMENTE RESIDENCIAL',
    'ESTRITAMENTE COMERCIAL / CORPORATIVO',
    'MISTO (RESIDENCIAL E COMERCIAL EQUILIBRADO)',
    'INSTITUCIONAL (ESCOLAS/HOSPITAIS/GOVERNO)',
    'INDUSTRIAL / LOGÍSTICO',
    'LAZER / ÁREAS VERDES / PRESERVAÇÃO',
  ],
  tendenciaMercado: [
    'VALORIZAÇÃO ACENTUADA (BOOM IMOBILIÁRIO)',
    'ESTABILIDADE DE PREÇOS E DEMANDA',
    'VALORIZAÇÃO MODERADA (CONSOLIDAÇÃO)',
    'DESVALORIZAÇÃO / SATURAÇÃO DE MERCADO',
    'ÁREA EM PROCESSO DE GENTRIFICAÇÃO',
    'TENDÊNCIA DE TRANSIÇÃO DE USO (RES PARA COM)',
  ],
};

const PADRAO_SISTEMA = {
  padraoOcupacao: 'PADRÃO MÉDIO-ALTO (CONDOMÍNIOS CLUB/CASAS AMPLAS)',
  verticalizacao: 'PREDOMINÂNCIA DE CASAS (BAIXA DENSIDADE)',
  conservacaoRegiao: 'CONSERVAÇÃO BOA (MANUTENÇÃO PERIÓDICA)',
  usoPreponderante: 'MISTO (RESIDENCIAL E COMERCIAL EQUILIBRADO)',
  tendenciaMercado: 'ESTABILIDADE DE PREÇOS E DEMANDA',
};

const ITENS_UI = [
  { id: 'padraoOcupacao',    label: 'Tipologia Predominante',    icon: Building,      desc: 'Nível socioeconômico e qualidade das edificações' },
  { id: 'verticalizacao',    label: 'Grau de Verticalização',    icon: Scaling,       desc: 'Densidade volumétrica e gabarito médio' },
  { id: 'conservacaoRegiao', label: 'Estado de Conservação',     icon: Construction,  desc: 'Zeladoria pública e manutenção do patrimônio' },
  { id: 'usoPreponderante',  label: 'Uso e Ocupação',            icon: Layout,        desc: 'Natureza das atividades econômicas e residenciais' },
  { id: 'tendenciaMercado',  label: 'Dinâmica de Valor',         icon: LandPlot,      desc: 'Comportamento histórico e projeção imobiliária' },
];

export default function VistoriaRegiaoFator06Ocupacao({ setNavegacao, infoLaudo, peritoAtivo }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [activePhotoSlot, setActivePhotoSlot] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [loadingCaptura, setLoadingCaptura] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const [dadosOcupacao, setDadosOcupacao] = useState({
    ...PADRAO_SISTEMA,
    fatorValorizacao: '1.00',
    textoConsolidado: '',
    justificativaDivergencia: '',
    evidencias: {},
    justificativas: {},
  });

  const divergencias = Object.keys(PADRAO_SISTEMA).filter(
    k => dadosOcupacao[k] !== PADRAO_SISTEMA[k]
  );

  const statusAuditoria = AuditoriaNormativaService.validarFinalizacao({
    ...dadosOcupacao,
    justificativaResumo: dadosOcupacao.justificativaDivergencia,
    _divergencias: divergencias,
  });

  useEffect(() => {
    const desvio = AuditoriaNormativaService.calcularDesvio(dadosOcupacao.fatorValorizacao);
    const sinal = desvio >= 0 ? '+' : '';

    const base =
      `Para a determinação do valor de mercado, aplicou-se o **Fator de Padrão de Ocupação**, ` +
      `justificando-se a valoração de **${sinal}${desvio.toFixed(2)}%** em virtude das características ` +
      `do entorno imediato. A tipologia predominante é ${dadosOcupacao.padraoOcupacao}, com ` +
      `verticalização ${dadosOcupacao.verticalizacao} e uso ${dadosOcupacao.usoPreponderante}. ` +
      `A tendência de mercado é ${dadosOcupacao.tendenciaMercado}. O cálculo seguiu a fórmula: ` +
      `$V_h = V_b \\times (1 + i)$.`;

    const adendo = dadosOcupacao.justificativaDivergencia
      ? `\n\n**Justificativa de Ocupação:** ${dadosOcupacao.justificativaDivergencia}`
      : '';

    const fechamento =
      `\n\nFundamentação: **NBR 14653-2:2011, Item ${FATOR_CONFIG.nbrItem}**.`;

    setDadosOcupacao(prev => ({
      ...prev,
      textoConsolidado: base + adendo + fechamento,
    }));
  }, [
    dadosOcupacao.fatorValorizacao,
    dadosOcupacao.justificativaDivergencia,
    dadosOcupacao.padraoOcupacao,
    dadosOcupacao.verticalizacao,
    dadosOcupacao.usoPreponderante,
    dadosOcupacao.tendenciaMercado,
  ]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoSlot) return;
    setErro(null);
    setLoadingCaptura(true);

    try {
      const resultado = await AssetPericialService.processarCaptura(
        file,
        FATOR_ID,
        infoLaudo?.numero || 'REPORT-F6',
        { perito: peritoAtivo, comodoLocal: activePhotoSlot, tentarGPS: true }
      );

      setDadosOcupacao(prev => ({
        ...prev,
        evidencias: {
          ...prev.evidencias,
          [activePhotoSlot]: {
            url: resultado.url,
            file: resultado.url,
            hashIntegridade: resultado.hashIntegridade,
            fileName: resultado.fileName,
            metadados: resultado.metadados,
          },
        },
      }));
      setShowPhotoOptions(false);
    } catch (err) {
      setErro(err.message || 'Falha ao processar captura');
    } finally {
      setLoadingCaptura(false);
      e.target.value = null;
    }
  };

  const salvarEAvancar = async () => {
    if (statusAuditoria.isBloqueado) {
      setErro(`BLOQUEIO PERICIAL: ${statusAuditoria.mensagem}`);
      return;
    }
    setIsSaving(true);
    setErro(null);

    try {
      await laudoApi.salvarModulo({
        report_id: infoLaudo?.numero || 'LAUDO-F06',
        perito: peritoAtivo || 'MONTE_HERMOM_USER',
        fator: FATOR_ID,
        codigo: FATOR_CONFIG.codigo,
        pasta: FATOR_CONFIG.pasta,
        data_execucao: new Date().toISOString(),
        statusRevisao: 'PENDENTE',
        ...dadosOcupacao,
      });
      // F06 é o último — vai direto para RESUMO (não proximaEtapa, para evitar retorno a F01)
      setNavegacao(ETAPA.RESUMO);
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente.');
        setTimeout(() => setNavegacao(ETAPA.RESUMO), 1200);
      } else {
        setErro(`Falha ao gravar: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isDivergente = (campo) => dadosOcupacao[campo] !== PADRAO_SISTEMA[campo];

  return (
    <div className="max-w-5xl mx-auto space-y-12 italic font-black relative pb-32 uppercase">
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[120px] z-0 select-none text-[#2F353B]">
        MONTE HERMOM
      </div>

      <input type="file" ref={cameraInputRef} onChange={onFileChange} accept="image/*" capture="environment" className="hidden" />
      <input type="file" ref={fileInputRef} onChange={onFileChange} accept="image/*" className="hidden" />

      <header className="flex justify-between items-end border-b-[10px] border-[#2F353B] pb-10 relative z-10">
        <div className="space-y-5">
          <div className="flex items-center gap-4 text-blue-600">
            <BrainCircuit size={28} className="animate-pulse" />
            <span className="text-[11px] tracking-[0.6em] text-slate-400 font-black uppercase">
              Auditoria de Ocupação Urbana
            </span>
          </div>
          <h2 className="text-[22px] text-[#2F353B] tracking-[0.25em] leading-none uppercase">
            {FATOR_CONFIG.titulo.split('. ')[1]} <span className="text-blue-600">URBANA</span>
          </h2>
        </div>
        <div className="bg-[#2F353B] px-10 py-5 rounded-[2rem] border-b-4 border-blue-600 shadow-2xl text-white text-[15px] font-black italic">
          REPORT Nº {infoLaudo?.numero || '2026-F6'}
        </div>
      </header>

      {erro && (
        <div className="bg-red-900 text-white p-6 rounded-[2rem] flex items-center gap-6 relative z-10">
          <ShieldAlert size={24} />
          <span className="text-[11px] font-black italic tracking-widest">{erro}</span>
        </div>
      )}

      <section className="space-y-8 relative z-10">
        {ITENS_UI.map((item) => {
          const divergente = isDivergente(item.id);
          const temFoto = !!dadosOcupacao.evidencias[item.id];

          return (
            <div
              key={item.id}
              className={`p-1.5 rounded-[5.5rem] border-4 transition-all duration-500 shadow-sm group ${
                divergente
                  ? 'bg-blue-50 border-blue-600'
                  : 'bg-white border-slate-100 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-8 p-5">
                <div className="flex items-center gap-8 w-[38%] border-r-4 border-slate-50 mr-8 py-2">
                  <div className={`p-6 rounded-[3rem] shadow-xl transition-all group-hover:rotate-6 ${divergente ? 'bg-blue-600 text-white' : 'bg-slate-50 text-blue-600'}`}>
                    <item.icon size={26} strokeWidth={2.5} />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[12px] tracking-widest leading-none font-black italic mb-2 uppercase">
                      {item.label}
                    </label>
                    <span className="text-[8px] text-slate-400 font-black uppercase tracking-widest leading-tight">
                      {item.desc}
                    </span>
                  </div>
                </div>

                <div className="flex-1">
                  <select
                    value={dadosOcupacao[item.id]}
                    onChange={(e) => setDadosOcupacao(prev => ({ ...prev, [item.id]: e.target.value }))}
                    className="w-full bg-slate-50 border-none rounded-[2.5rem] p-7 text-slate-800 text-[13px] outline-none shadow-inner font-black italic appearance-none cursor-pointer hover:bg-slate-100 transition-all uppercase"
                  >
                    {DICIONARIO_ABNT[item.id].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={() => { setActivePhotoSlot(item.id); setShowPhotoOptions(true); }}
                    className={`p-6 rounded-full border-4 transition-all ${
                      temFoto
                        ? 'bg-blue-600 border-blue-600 text-white shadow-xl'
                        : 'border-slate-100 text-slate-200 hover:text-blue-600 hover:border-blue-600'
                    }`}
                  >
                    {temFoto ? <CheckCircle2 size={24} /> : <Camera size={24} />}
                  </button>
                </div>
              </div>

              {divergente && !temFoto && (
                <div className="mt-4 ml-24 mb-6">
                  <textarea
                    value={dadosOcupacao.justificativas[item.id] || ''}
                    onChange={(e) => setDadosOcupacao(prev => ({
                      ...prev,
                      justificativas: { ...prev.justificativas, [item.id]: e.target.value },
                    }))}
                    placeholder="Fundamentação técnica para ausência de evidência..."
                    className="w-full bg-white border-2 border-amber-200 rounded-[2.5rem] p-6 text-[11px] italic font-black text-slate-600 outline-none focus:border-amber-500"
                    rows={2}
                  />
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="bg-[#2F353B] p-16 rounded-[6rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border-b-[20px] border-blue-600 relative overflow-hidden mt-10 z-10">
        <div className="relative z-10 space-y-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-[#1e293b] p-12 rounded-[4rem] border-2 border-white/5 text-center shadow-inner">
              <label className="text-[11px] text-blue-400 block mb-6 italic font-black uppercase tracking-[0.4em]">
                Fator de Ocupação (i)
              </label>
              <div className="flex items-center justify-center gap-4">
                <Scale className="text-slate-500" size={24} />
                <input
                  type="text"
                  value={dadosOcupacao.fatorValorizacao}
                  onChange={(e) => setDadosOcupacao(prev => ({ ...prev, fatorValorizacao: e.target.value }))}
                  className="w-full bg-slate-900/50 py-8 rounded-[3rem] text-[32px] text-white italic outline-none text-center tracking-[0.4em] font-black border-2 border-transparent focus:border-blue-600 transition-all"
                />
              </div>
            </div>
            <div className="bg-blue-600 p-12 rounded-[4rem] text-center shadow-2xl flex flex-col justify-center items-center relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                <History size={40} />
              </div>
              <p className="text-white/80 text-[12px] mb-6 font-black italic">
                $$V_h = V_b \times (1 + i)$$
              </p>
              <h4 className="text-[18px] tracking-[0.3em] text-white italic font-black uppercase">
                Homogeneização Normativa
              </h4>
            </div>
          </div>

          <div className="bg-white/5 p-12 rounded-[5rem] border-2 border-white/5 space-y-8 backdrop-blur-md">
            <div className="flex items-center gap-6 border-b border-white/10 pb-8 font-black italic text-white">
              <Fingerprint className="text-blue-600" size={24} strokeWidth={3} />
              <h3 className="text-[16px] tracking-[0.3em] uppercase">Parecer Técnico de Entorno</h3>
            </div>
            <textarea
              readOnly
              value={dadosOcupacao.textoConsolidado}
              className="w-full h-56 bg-transparent border-none text-slate-300 text-[13px] leading-[2] resize-none font-black italic outline-none text-justify"
            />
          </div>

          {divergencias.length > 0 && (
            <div className="pt-10 border-t-2 border-white/10">
              <div className="flex items-center gap-4 mb-6">
                <AlertTriangle size={18} className="text-amber-500" />
                <label className="text-[11px] text-amber-400 font-black italic uppercase tracking-widest">
                  Justificativa de Ajuste
                </label>
              </div>
              <textarea
                value={dadosOcupacao.justificativaDivergencia}
                onChange={(e) => setDadosOcupacao(prev => ({ ...prev, justificativaDivergencia: e.target.value }))}
                placeholder="FUNDAMENTE O MOTIVO DA VALORAÇÃO DIFERENCIADA..."
                className="w-full h-36 bg-white/5 rounded-[3rem] p-8 text-white text-[12px] font-black italic outline-none border-2 border-amber-900/30 focus:border-amber-600 transition-all"
              />
            </div>
          )}
        </div>
        <Database className="absolute -right-32 -bottom-32 text-white/[0.02] w-[450px] h-[450px] -rotate-12 pointer-events-none" />
      </section>

      <footer className="flex justify-between items-center pt-12 border-t-4 border-slate-100 mt-20 relative z-10">
        <div className="flex flex-col font-black italic uppercase">
          <span className="text-[13px] text-[#2F353B] tracking-[0.6em]">MONTE HERMOM</span>
          <span className="text-[9px] text-blue-600 mt-3 font-black uppercase tracking-[0.3em]">
            Integridade PGLP • 2026
          </span>
        </div>

        <button
          onClick={salvarEAvancar}
          disabled={isSaving}
          className="group flex items-center gap-14 bg-[#2F353B] text-white px-24 py-11 rounded-[5.5rem] shadow-2xl transition-all text-[18px] font-black italic hover:bg-blue-600 disabled:opacity-50"
        >
          {isSaving ? 'PROCESSANDO...' : 'CONCLUIR ANÁLISE REGIONAL'}
          <ChevronRight size={28} className="group-hover:translate-x-6 transition-transform" />
        </button>
      </footer>

      {showPhotoOptions && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-[#2F353B]/98 backdrop-blur-2xl p-10">
          <div className="bg-white w-full max-w-xl rounded-[6rem] p-16 space-y-12 border-[10px] border-blue-600 shadow-2xl text-center">
            <div className="bg-blue-600 w-24 h-24 rounded-full flex items-center justify-center mx-auto text-white shadow-xl">
              <Camera size={40} />
            </div>
            <div>
              <h3 className="text-[20px] font-black italic uppercase tracking-widest text-[#2F353B]">
                Admissão Visual Pericial
              </h3>
              <p className="text-[10px] text-slate-400 font-black italic tracking-widest uppercase mt-2">
                Fotodocumentação de Entorno e Ocupação
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-[#2F353B] text-white p-11 rounded-[3.5rem] flex items-center justify-center gap-6 font-black italic hover:bg-blue-600 disabled:opacity-50"
              >
                <Zap size={24} /> {loadingCaptura ? 'VALIDANDO...' : 'ATIVAR LENTE DE CAMPO'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-white border-4 border-slate-100 text-slate-400 p-11 rounded-[3.5rem] flex items-center justify-center gap-6 font-black italic hover:border-blue-600 hover:text-blue-600 disabled:opacity-50"
              >
                <ImageIcon size={24} /> IMPORTAR ATIVO DIGITAL
              </button>
            </div>
            <button
              onClick={() => { setShowPhotoOptions(false); setActivePhotoSlot(null); }}
              className="w-full text-[11px] text-slate-300 font-black italic tracking-[0.6em] uppercase hover:text-red-600 transition-colors"
            >
              CANCELAR OPERAÇÃO
            </button>
          </div>
        </div>
      )}
    </div>
  );
}